import { ColumnBodyOptions } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import enumService from '../../service/EnumService';
import { Tag } from 'primereact/tag';
import { format } from 'date-fns';
import { Chip } from 'primereact/chip';
import { Calendar } from 'primereact/calendar';
import { useState } from 'react';
import { TriStateCheckbox } from 'primereact/tristatecheckbox';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { SuggestionOutput } from '../../types/Suggestion';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import suggestionsService from '../../service/api/SuggestionsService';

const AutocompleteFilter = ({ value, onChange, name, entity, t }) => {
  const [suggestions, setSuggestions] = useState<SuggestionOutput[]>([]);

  const search = async (event: any) => {
    const results = await suggestionsService.getSuggestions({ query: event.query, entity });
    setSuggestions(results);
  };

  return (
    <AutoComplete
      value={value}
      suggestions={suggestions}
      completeMethod={search}
      field="value"
      multiple
      dropdown
      onChange={(e) => onChange(name, e.value)}
      placeholder={t('common.select')}
      className="p-column-filter"
      style={{ maxWidth: '24rem' }}
    />
  );
};

export const getFilterTemplates = (t) => {
  const yesNoOptions = enumService.getYesNo(t);
  const okNokOptions = enumService.getOkNok(t);

  const yesNoFilterTemplate = (value, onChange, name) => {
    return (
      <Dropdown
        value={value}
        onChange={(e) => onChange(name, e.value)}
        options={yesNoOptions}
        placeholder={t('common.select')}
      />
    );
  };

  const okNokFilterTemplate = (value, onChange, name) => {
    return (
      <Dropdown
        value={value}
        onChange={(e) => onChange(name, e.value)}
        options={okNokOptions}
        placeholder={t('common.select')}
      />
    );
  };

  const stringFilterTemplate = (value, onChange, name) => {
    return <InputText value={value} onChange={(e) => onChange(name, e.target.value)} />;
  };

  const multiLineFilterTemplate = (value, onChange, name) => {
    return (
      <>
        <InputTextarea value={value} onChange={(e) => onChange(name, e.target.value)} rows={3} />
      </>
    );
  };

  const boolFilterTemplate = (value, onChange, name) => {
    return <TriStateCheckbox value={value} onChange={(e) => onChange(name, e.value)} />;
  };

  // this component uses single calendar in a range mode, currently not used
  // but kept for possible future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const DateRangeFilter = ({ value, onChange, name }) => {
    const [visible, setVisible] = useState(false);

    const handleChange = (e) => {
      onChange(name, e.value);
      //close when range is selected
      if (Array.isArray(e.value) && e.value[0] && e.value[1]) {
        setVisible(false);
      }
    };

    return (
      <>
        {/* When calendar receives focus automatically, when filter is opened, for some reason it is displayed behind the filter
       so as a quick workaround there is another focusable element as a first child */}
        <label tabIndex={0}></label>
        <Calendar
          visible={visible}
          value={value}
          onChange={handleChange}
          selectionMode="range"
          onVisibleChange={(e) => setVisible(e.visible)}
          placeholder={t('common.select_date_range')}
        />
      </>
    );
  };

  const DateRangeFilterSeparate = ({ value, onChange, name }) => {
    const handleChange = (e, field) => {
      const actualValue = field === 'from' ? [e.value, value?.[1] || null] : [value?.[0] || null, e.value];
      onChange(name, actualValue);
    };

    return (
      <>
        <div>
          {/* When calendar receives focus automatically, when filter is opened, for some reason it is displayed behind the filter
       so as a quick workaround there is another focusable element as a first child */}
          <label tabIndex={0} htmlFor={`from-${name}`}>
            {t('common.from')}
          </label>
          <Calendar
            id={`from-${name}`}
            value={value?.[0] || null}
            onChange={(e) => handleChange(e, 'from')}
            placeholder={t('common.from')}
            showIcon
          />
        </div>
        <div className="mt-2">
          <label htmlFor={`to-${name}`}>{t('common.to')}</label>
          <Calendar
            id={`to-${name}`}
            value={value?.[1] || null}
            onChange={(e) => handleChange(e, 'to')}
            placeholder={t('common.to')}
            showIcon
          />
        </div>
      </>
    );
  };

  const dateRangeFilterTemplate = (value, onChange, name) => {
    return <DateRangeFilterSeparate value={value} onChange={onChange} name={name} />;
  };

  // TODO: configure whether to include min and max (so <= vs <)?
  const NumberRangeFilter = ({ value, onChange, name, locale, maxFractionDigits }) => {
    const handleChange = (e, field) => {
      const actualValue = field === 'min' ? [e.value, value?.[1] || null] : [value?.[0] || null, e.value];
      onChange(name, actualValue);
    };

    return (
      <>
        <div>
          <label htmlFor={`min-${name}`}>{t('common.min')}</label>
          <InputNumber
            id={`min-${name}`}
            locale={locale}
            maxFractionDigits={maxFractionDigits}
            value={value?.[0] || null}
            onValueChange={(e) => handleChange(e, 'min')}
          />
        </div>
        <div className="mt-2">
          <label htmlFor={`max-${name}`}>{t('common.max')}</label>
          <InputNumber
            id={`max-${name}`}
            locale={locale}
            maxFractionDigits={maxFractionDigits}
            value={value?.[1] || null}
            onValueChange={(e) => handleChange(e, 'max')}
          />
        </div>
      </>
    );
  };

  const numberRangeFilterTemplate = (value, onChange, name, maxFractionDigits, locale = undefined) => {
    return (
      <NumberRangeFilter
        value={value}
        onChange={onChange}
        name={name}
        locale={locale}
        maxFractionDigits={maxFractionDigits}
      />
    );
  };

  const multiselectFilterTemplate = (value, onChange, name, availableOptions, labelField = 'label') => {
    return (
      <>
        <MultiSelect
          value={value}
          options={availableOptions}
          onChange={(e) => onChange(name, e.value)}
          optionLabel={labelField}
          placeholder={t('common.select')}
          maxSelectedLabels={1}
          selectedItemsLabel={t('common.selected', { count: value?.length })}
          className="p-column-filter"
        />
      </>
    );
  };

  const autocompleteFilterTemplate = (value, onChange, name, entity) => {
    return <AutocompleteFilter value={value} onChange={onChange} name={name} entity={entity} t={t} />;
  };

  return {
    yesNoFilterTemplate,
    okNokFilterTemplate,
    boolFilterTemplate,
    stringFilterTemplate,
    multiLineFilterTemplate,
    dateRangeFilterTemplate,
    multiselectFilterTemplate,
    numberRangeFilterTemplate,
    autocompleteFilterTemplate,
  };
};

export const getColumnTemplates = (t) => {
  const bodyTemplate = (data: any, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const trimmedTextTemplate = (data: any, props: ColumnBodyOptions, maxLength: number) => {
    const value = data[props.field];
    const trimmedValue = value && value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
    return <>{trimmedValue}</>;
  };

  const numberTemplate = (data: any, props: ColumnBodyOptions, unitField?: string) => {
    const value = data[props.field];
    const unit = unitField ? data[unitField] : null;
    const valueFormatted = value != null ? parseFloat(value.toFixed(3)) : '';
    return (
      <>
        {valueFormatted}
        {unit ? ` ${unit}` : ''}
      </>
    );
  };

  const boolColumnTemplate = (data: any, props: ColumnBodyOptions, trueValue: string, falseValue: string) => {
    let result: any = '';

    if (data[props.field] === true) {
      result = <Tag className="mr-2" icon="pi pi-check" severity="success" value={trueValue}></Tag>;
    } else if (data[props.field] === false) {
      result = <Tag className="mr-2" icon="pi pi-times-circle" severity="danger" value={falseValue}></Tag>;
    }
    return result;
  };

  const enumColumnTemplate = (data: any, props: ColumnBodyOptions, translationPrefix: string) => {
    const field = data[props.field];
    return <>{field ? t(`${translationPrefix}.${field}`) : ''}</>;
  };

  const multiEnumColumnTemplate = (data: any, props: ColumnBodyOptions, translationPrefix: string) => {
    const field = data[props.field];

    return (
      <>
        {field?.map((item) => {
          return <Chip key={item} label={t(`${translationPrefix}.${item}`)} className="mr-2 mb-2" />;
        })}
      </>
    );
  };

  const multiReferenceColumnTemplate = (data: any, props: ColumnBodyOptions) => {
    const field = data[props.field];

    return (
      <>
        {field?.map((item: SuggestionOutput) => {
          return <Chip key={item.id} label={item.value} className="mr-2 mb-2" />;
        })}
      </>
    );
  };

  const calendarColumnTemplate = (data: any, props: ColumnBodyOptions, dateFormat: string, emptyValue = '-') => {
    const value = data[props.field];
    const formattedValue = value ? format(value, dateFormat) : emptyValue;
    return <>{formattedValue}</>;
  };

  const calendarValueColumnTemplate = (value: Date, dateFormat: string, emptyValue = '-') => {
    const formattedValue = value ? format(value, dateFormat) : emptyValue;
    return <>{formattedValue}</>;
  };

  return {
    bodyTemplate,
    trimmedTextTemplate,
    numberTemplate,
    boolColumnTemplate,
    enumColumnTemplate,
    multiEnumColumnTemplate,
    multiReferenceColumnTemplate,
    calendarColumnTemplate,
    calendarValueColumnTemplate,
  };
};
